import { useLocation } from "react-router-dom";
import BugReportForm, { DiagnosticLogs } from "@jkast90/kast-common/bug-report-form";
import { isSignedIn, useSession } from "@jkast90/kast-common/auth";
import { session, signIn } from "../auth/session";
import { useAdminMode } from "@common/adminMode";

/** CloudBoy's id in the kast-dev bug report inbox: its deploy and metadata prefix. */
export const BUG_REPORT_APP = "cloudboy";

const FORM_CLASSES = {
  button: "btn",
  primaryButton: "btn primary",
  input: "input",
  label: "field-label",
  hint: "muted small",
};

/**
 * The session's bearer, refreshed first when it has expired (a laptop
 * that slept past the scheduled refresh): the inbox refuses an expired one.
 */
export async function bugReportToken(): Promise<string | null> {
  const { user } = session.getState();
  if (user && user.exp * 1000 <= Date.now()) await session.refresh();
  return session.bearer();
}

interface Props {
  user: { id: string | null; email: string | null } | null;
  /** The logs are shown beside the form to admins with admin tools on. */
  admin: boolean;
  onSignIn: () => void;
  /** The crash an ErrorBoundary handed over. */
  initialDescription?: string;
}

/**
 * The report form, with no router or store hooks, so the app-level crash
 * screen can show it outside the providers. Reports go to the kast-dev
 * admin inbox, not CloudBoy's API.
 */
export function BugReport({ user, admin, onSignIn, initialDescription = "" }: Props) {
  return (
    <div className="about-main">
      <div className="library-head">
        <h1>Report a bug</h1>
      </div>
      <section className="panel">
        <BugReportForm
          app={BUG_REPORT_APP}
          user={user}
          getToken={bugReportToken}
          onSignIn={onSignIn}
          initialDescription={initialDescription}
          classNames={FORM_CLASSES}
        />
      </section>
      {admin && (
        <section className="panel">
          <h2>Attached logs</h2>
          <DiagnosticLogs classNames={FORM_CLASSES} />
        </section>
      )}
    </div>
  );
}

/** /bug-report: pre-filled when arriving from a crash screen's Report This. */
export default function BugReportPage() {
  const location = useLocation();
  // Not `user`, which a session still loading already has.
  const state = useSession();
  const user = isSignedIn(state) ? state.user : null;
  const { unlocked } = useAdminMode();
  const crashNote = (location.state as { crashNote?: string } | null)?.crashNote ?? "";
  return (
    <BugReport
      user={user ? { id: user.sub, email: user.email || null } : null}
      admin={unlocked}
      onSignIn={signIn}
      initialDescription={crashNote}
    />
  );
}
